import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { UploadProvider, useUploads } from './lib/upload-manager.js';
import { formatBytes, api, loginUrl, type Me } from './lib/api.js';
import { BrowsePage } from './pages/BrowsePage.js';
import { AssetPage } from './pages/AssetPage.js';
import { UploadPage } from './pages/UploadPage.js';
import { EditPage } from './pages/EditPage.js';
import { PeoplePage } from './pages/PeoplePage.js';
import { LinkPage } from './pages/LinkPage.js';
import { AdminPage } from './pages/AdminPage.js';

interface SessionValue {
  me: Me | null;
  loading: boolean;
  refresh: () => void;
}

const SessionContext = createContext<SessionValue>({
  me: null,
  loading: true,
  refresh: () => {},
});

export const useSession = (): SessionValue => useContext(SessionContext);

/** Tier thresholds mirrored from the server's CAPABILITIES map. */
export const TIER = {
  viewer: 10,
  member: 20,
  editor: 30,
  lead: 40,
  admin: 50,
} as const;

const LOGIN_ERRORS: Record<string, string> = {
  not_in_guild:
    'Your Discord account is not a member of the team server. Ask a lead for an invite, then try again.',
  no_access:
    'Your account is in the server but has not been granted access yet. Ask an admin to assign you a role.',
  access_denied: 'You cancelled the Discord login.',
};

function LoginScreen({ error }: { error: string | null }): ReactNode {
  const location = useLocation();
  // Preserve where they were heading so a deep link survives the login round
  // trip. `next` is validated server-side to reject absolute URLs.
  const next = location.pathname === '/' ? undefined : location.pathname + location.search;

  return (
    <div className="login-screen">
      <div className="login-card">
        <h1 style={{ marginTop: 0 }}>Team Media Archive</h1>
        <p style={{ color: 'var(--text-dim)' }}>
          Sign in with the Discord account you use in the team server.
        </p>
        {error && (
          <div className="notice error" style={{ textAlign: 'left' }}>
            {LOGIN_ERRORS[error] ?? `Login failed: ${error}`}
          </div>
        )}
        <a className="btn primary" href={loginUrl(next)} style={{ display: 'inline-block' }}>
          Continue with Discord
        </a>
      </div>
    </div>
  );
}

/**
 * Live upload progress in the header.
 *
 * Uploads keep running when you navigate away, which is the point -- but that
 * only helps if you can still see them. Without this the archive looks idle
 * while 300 GB is moving.
 */
function UploadIndicator(): ReactNode {
  const { activeCount, failedCount, progress } = useUploads();
  if (activeCount === 0 && failedCount === 0) return null;

  const pct =
    progress.total > 0 ? Math.min(100, Math.round((progress.sent / progress.total) * 100)) : 0;

  return (
    <NavLink
      to="/upload"
      className="upload-indicator"
      // Bytes belong in the tooltip rather than the bar: during a large
      // migration "8%" says far less than how much is actually left.
      title={
        activeCount > 0
          ? `${formatBytes(progress.sent)} of ${formatBytes(progress.total)} uploaded`
          : `${failedCount} upload(s) need attention`
      }
    >
      {activeCount > 0 ? (
        <>
          <span className="upload-indicator-dot" />
          {activeCount} uploading · {pct}%
        </>
      ) : (
        <span style={{ color: 'var(--danger)' }}>{failedCount} upload(s) failed</span>
      )}
    </NavLink>
  );
}

export function App(): ReactNode {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const loginError = new URLSearchParams(location.search).get('error');

  const refresh = (): void => {
    setLoading(true);
    api
      .me()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  if (loading) {
    return <div className="empty">Loading…</div>;
  }

  if (!me) {
    return <LoginScreen error={loginError} />;
  }

  const isAdmin = me.tier >= TIER.admin;
  const canUpload = me.tier >= TIER.member;
  const canEdit = me.tier >= TIER.editor;

  return (
    <SessionContext.Provider value={{ me, loading, refresh }}>
      <UploadProvider>
      <div className="app">
        <header className="header">
          <h1>Media Archive</h1>
          <nav>
            <NavLink to="/" end>
              Browse
            </NavLink>
            {canUpload && <NavLink to="/upload">Upload</NavLink>}
            <NavLink to="/people">People</NavLink>
            {canEdit && <NavLink to="/edit">Auto-edit</NavLink>}
            <NavLink to="/link">Devices</NavLink>
            {isAdmin && <NavLink to="/admin">Admin</NavLink>}
          </nav>
          <div className="spacer" />
          <UploadIndicator />
          <span className="tier-pill">{me.tierName}</span>
          <img className="avatar" src={me.avatarUrl} alt="" />
          <span style={{ fontSize: 13 }}>{me.displayName}</span>
          <button
            className="btn sm"
            onClick={() => {
              void api.logout().then(() => window.location.reload());
            }}
          >
            Sign out
          </button>
        </header>

        <Routes>
          <Route path="/" element={<BrowsePage />} />
          <Route path="/folder/:folderId" element={<BrowsePage />} />
          <Route path="/asset/:assetId" element={<AssetPage />} />
          <Route
            path="/upload"
            element={canUpload ? <UploadPage /> : <Navigate to="/" replace />}
          />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/edit" element={canEdit ? <EditPage /> : <Navigate to="/" replace />} />
          <Route path="/link" element={<LinkPage />} />
          <Route path="/admin" element={isAdmin ? <AdminPage /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      </UploadProvider>
    </SessionContext.Provider>
  );
}
